/**
 * Submission management service
 */
import { api } from './api';

export interface SubmissionDocument {
  id: number;
  filename: string;
  file_size: number;
  uploaded_at: string;
  processing_status: string;
  processing_status_display?: string;
  document_type?: string;
}

export interface Submission {
  id: number;
  vendor: number;
  vendor_name: string;
  program: number;
  program_name: string;
  technology: number | null;
  status: string;
  status_display: string;
  submission_stage: string;
  submission_date: string | null;
  submission_contact_name: string;
  submission_contact_email: string;
  submission_contact_phone: string;
  last_updated: string;
  created_at: string;
  metadata: Record<string, any>;
  documents_count?: number;
  documents?: SubmissionDocument[]; // Only included in detail view
}

export interface SubmitSubmissionData {
  submission_contact_name: string;
  submission_contact_email: string;
  submission_contact_phone?: string;
  metadata?: Record<string, any>;
}

export interface ExtractedMetadata {
  company_name: string | null;
  website: string | null;
  company_address: string | null;
  contact_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  linkedin_url: string | null;
  team_members?: Array<{
    name: string;
    role?: string;
    linkedin?: string;
  }>;
}

export interface TeamMember {
  id: number;
  vendor: number;
  name: string;
  role: string;
  email: string;
  phone: string;
  linkedin_url: string;
  bio: string;
  is_primary_contact: boolean;
  extracted_from_document: boolean;
  added_at: string;
}

export interface CompletenessAssessment {
  id: string; // ai.Job UUID (was a numeric CompletenessAssessment PK pre-DEV-732)
  submission: number;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  overall_completeness: number | null;
  assessment_data: {
    overall_completeness: number;
    sections: Array<{
      section_title: string;
      is_addressed: boolean;
      confidence: 'high' | 'medium' | 'low';
      notes: string;
      missing_info: string[] | null;
    }>;
  };
  celery_task_id?: string;
  error_message?: string;
  triggered_at: string;
  completed_at: string | null;
  updated_at: string;
}

export class SubmissionService {
  /**
   * Get or create a draft submission for a program
   */
  async getOrCreateDraft(programId: number): Promise<{ submission: Submission; created: boolean }> {
    return api.post(`/api/submissions/get_or_create_draft/`, { program_id: programId });
  }

  /**
   * Submit a draft submission (change status from 'draft' to 'submitted')
   */
  async submitDraft(submissionId: number, data: SubmitSubmissionData): Promise<Submission> {
    return api.post(`/api/submissions/${submissionId}/submit/`, data);
  }

  /**
   * Get a single submission by ID
   */
  async get(submissionId: number): Promise<Submission> {
    return api.get(`/api/submissions/${submissionId}/`);
  }

  /**
   * List submissions with optional filters
   */
  async list(params?: { status?: string; program?: number }): Promise<Submission[]> {
    return api.get('/api/submissions/', params);
  }

  /**
   * Update a submission
   */
  async update(submissionId: number, data: Partial<Submission>): Promise<Submission> {
    return api.patch(`/api/submissions/${submissionId}/`, data);
  }

  /**
   * Trigger metadata extraction from documents
   */
  async extractMetadata(submissionId: number): Promise<{ task_id: string; message: string }> {
    return api.post(`/api/submissions/${submissionId}/extract_metadata/`);
  }

  /**
   * Get extracted metadata (polls for completion)
   */
  async getMetadata(submissionId: number): Promise<{
    status: 'not_started' | 'completed' | 'failed';
    data?: ExtractedMetadata;
    completed_at?: string;
    error?: string;
    failed_at?: string;
  }> {
    return api.get(`/api/submissions/${submissionId}/get_metadata/`);
  }

  /**
   * Trigger completeness assessment
   */
  async assessCompleteness(submissionId: number): Promise<{ task_id: string; message: string }> {
    return api.post(`/api/submissions/${submissionId}/assess_completeness/`);
  }

  /**
   * Get completeness assessment (polls for completion)
   */
  async getCompleteness(submissionId: number): Promise<{
    status: 'not_started' | 'pending' | 'processing' | 'completed' | 'failed';
    data?: CompletenessAssessment;
    overall_completeness?: number;
    completed_at?: string;
    error_message?: string;
    triggered_at?: string;
  }> {
    return api.get(`/api/submissions/${submissionId}/get_completeness/`);
  }

  /**
   * Get team members for the submission's vendor
   */
  async getTeamMembers(submissionId: number): Promise<{
    extracted_members: TeamMember[];
    manual_members: TeamMember[];
    total_count: number;
    extracted_count: number;
  }> {
    return api.get(`/api/submissions/${submissionId}/get_team_members/`);
  }

  /**
   * Poll for metadata extraction completion
   */
  async pollMetadata(
    submissionId: number,
    onUpdate: (data: ExtractedMetadata | null) => void,
    maxAttempts: number = 60,
    intervalMs: number = 2000
  ): Promise<ExtractedMetadata | null> {
    let attempts = 0;

    return new Promise((resolve, reject) => {
      const poll = async () => {
        try {
          const result = await this.getMetadata(submissionId);

          if (result.status === 'completed' && result.data) {
            onUpdate(result.data);
            resolve(result.data);
            return;
          }

          attempts++;
          if (attempts >= maxAttempts) {
            reject(new Error('Polling timeout'));
            return;
          }

          setTimeout(poll, intervalMs);
        } catch (error) {
          reject(error);
        }
      };

      poll();
    });
  }
}

export const submissionService = new SubmissionService();

